import { Box, Stack, Typography } from '@mui/material';
import { ComponentType } from 'react';
import * as React from 'react';

import { IconProps } from '@/components/icons/ChevronDownIcon';
import { TypographyClamped } from '@/components/typography/TypographyClamped';
import { useIsElementHovered } from '@/hooks/useIsElementHovered';
import { COLORS } from '@/styles/tokens/colors';

import { getRelativeTime } from './NotificationItem.utils';
import { NotificationItemActions } from './NotificationItemActions';
import { UnreadIndicator } from './UnreadIndicator';

export type NotificationItemVariant = 'normal' | 'urgent';

export interface NotificationItemProps {
  Icon: ComponentType<IconProps>;
  title: React.ReactNode;
  householdName?: string;
  description: string;
  timestamp: Date;
  variant: NotificationItemVariant;
  isUnread: boolean;
  onClick: () => void;
  onDelete: () => void;
  onToggleRead: () => void;
}

/**
 * @description NotificationItem is a single item in the notification tray. It is purely presentational.
 */
export function NotificationItem({
  Icon,
  title,
  description,
  householdName,
  isUnread,
  onDelete,
  onToggleRead,
  onClick,
  timestamp,
  variant,
}: NotificationItemProps) {
  const [isHovered, elementProps] = useIsElementHovered();
  return (
    <Stack
      component="button"
      px={2}
      py={1.5}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      spacing={2}
      onClick={onClick}
      sx={{
        border: 0,
        backgroundColor: isHovered ? COLORS.FUNCTIONAL.HOVER : 'transparent',
        cursor: 'pointer',
      }}
      {...elementProps}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-start"
        flexGrow={1}
        minWidth={0}
        spacing={2}
      >
        <Box>
          <UnreadIndicator color="error" show={isUnread}>
            <Box
              sx={{
                ...(variant === 'urgent' && {
                  backgroundColor: COLORS.FUNCTIONAL.WARNING[50],
                  borderRadius: '4px',
                  padding: '3px',
                  height: 26,
                  width: 26,
                }),
              }}
            >
              <Icon
                size={16}
                color={
                  variant === 'urgent'
                    ? COLORS.FUNCTIONAL.WARNING[400]
                    : COLORS.TEXT.SECONDARY
                }
              />
            </Box>
          </UnreadIndicator>
        </Box>
        <Stack
          textAlign="left"
          alignItems="flex-start"
          minWidth={0}
          spacing={0.25}
        >
          {householdName && (
            <Typography variant="eyebrow-xs" component="span">
              {householdName}
            </Typography>
          )}

          <Box minWidth={0}>
            <Typography variant="title-xs" component="span">
              {title}
            </Typography>
            <TypographyClamped variant="body-xs" component="span" lines={2}>
              {description}
            </TypographyClamped>
          </Box>
        </Stack>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          flexShrink: 0,
          alignItems: 'center',
          justifyItems: 'end',
          // Both children occupy the same grid cell so the container
          // always reserves the width of the wider element (the action
          // buttons), preventing the left-side text from reflowing on hover.
          '& > *': { gridColumn: 1, gridRow: 1 },
        }}
      >
        <Box sx={{ visibility: isHovered ? 'hidden' : 'visible' }}>
          <Timestamp timestamp={timestamp} />
        </Box>
        <Box sx={{ visibility: isHovered ? 'visible' : 'hidden' }}>
          <NotificationItemActions
            isUnread={isUnread}
            onDelete={onDelete}
            onToggleRead={onToggleRead}
          />
        </Box>
      </Box>
    </Stack>
  );
}

interface TimestampProps {
  timestamp: Date;
}

function Timestamp({ timestamp }: TimestampProps) {
  return (
    <Typography variant="body-xs">{getRelativeTime(timestamp)}</Typography>
  );
}
